/* t13 independent diagnostics. Functional modules do not import this file. */
(() => {
  'use strict';
  if (window.DawnBaseDebug) return;
  const VERSION = 'Dawn-base-t13-debug-1.0.0';
  const enabled = new URLSearchParams(location.search).get('theme_debug') === '1';
  let active = false;
  let sequence = 0;
  let records = [];
  let discarded = 0;
  let observer;
  let timer;
  let cleanup = [];
  let originalFetch;
  let wrappedFetch;
  let originalOpen;
  let wrappedOpen;
  let originalSend;
  let wrappedSend;
  const xhrMeta = new WeakMap();

  const now = () => Math.round(performance.now());
  const safe = (fn, fallback) => { try { return fn(); } catch { return fallback; } };
  const log = (type, detail = {}, level = 'info') => {
    if (!active) return;
    records.push({ n: ++sequence, t: now(), type, level, ...detail });
    if (records.length > 800) { records.shift(); discarded += 1; }
  };
  const classify = input => {
    const value = typeof input === 'string' || input instanceof URL ? input : input?.url;
    let url;
    try { url = new URL(value, location.href); } catch { return { kind: 'unknown' }; }
    const sameOrigin = url.origin === location.origin;
    const kind = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/.test(url.pathname)
      ? `cart:${url.pathname.match(/\/cart\/([^/.]+)/)[1]}`
      : /\/cart(\.js|\.json)?$/.test(url.pathname) ? 'cart:read'
      : url.searchParams.has('sections') ? 'sections'
      : /\/checkouts?(\/|$)/.test(url.pathname) ? 'checkout'
      : /\/recommendations/.test(url.pathname) ? 'recommendation'
      : 'resource';
    return { kind, sameOrigin, host: sameOrigin ? 'same-origin' : url.hostname, path: url.pathname };
  };
  const featureSnapshot = () => [...document.querySelectorAll('[data-dawn-base-feature]')].map(node => ({
    feature: node.dataset.dawnBaseFeature || '',
    tag: node.localName,
    visible: !!node.getClientRects().length,
    section: node.closest('.shopify-section')?.id || ''
  }));
  const snapshot = () => ({
    features: featureSnapshot(),
    drawers: [...document.querySelectorAll('cart-drawer')].map(node => ({
      open: node.classList.contains('active'),
      rows: node.querySelectorAll('.cart-item').length,
      empty: !!node.querySelector('.drawer__inner-empty')
    })),
    forms: document.querySelectorAll('form[action*="/contact"], form[action*="/cart"]').length,
    duplicateIds: (() => {
      const seen = new Set();
      const duplicates = new Set();
      document.querySelectorAll('[id]').forEach(node => {
        if (seen.has(node.id)) duplicates.add(node.id);
        seen.add(node.id);
      });
      return [...duplicates].slice(0, 30);
    })()
  });
  const attach = (target, type, handler, options) => {
    const safeHandler = event => safe(() => handler(event));
    target.addEventListener(type, safeHandler, options);
    cleanup.push(() => target.removeEventListener(type, safeHandler, options));
  };
  const inspectResponse = response => ({ status: response.status, route: classify(response.url) });
  const installNetwork = () => {
    if (typeof window.fetch === 'function') {
      originalFetch = window.fetch;
      wrappedFetch = function(...args) {
        const meta = classify(args[0]);
        log('request.start', { transport: 'fetch', route: meta });
        const promise = Reflect.apply(originalFetch, this, args);
        promise.then(response => log('request.result', { transport: 'fetch', ...inspectResponse(response) }), () => log('request.failure', { transport: 'fetch', route: meta }, 'error')).catch(() => {});
        return promise;
      };
      window.fetch = wrappedFetch;
    }
    if (!window.XMLHttpRequest) return;
    originalOpen = XMLHttpRequest.prototype.open;
    originalSend = XMLHttpRequest.prototype.send;
    wrappedOpen = function(method, url, ...args) {
      xhrMeta.set(this, { method, route: classify(url) });
      return Reflect.apply(originalOpen, this, [method, url, ...args]);
    };
    wrappedSend = function(...args) {
      const meta = xhrMeta.get(this) || { method: 'unknown', route: { kind: 'unknown' } };
      log('request.start', { transport: 'xhr', method: meta.method, route: meta.route });
      this.addEventListener('loadend', () => log('request.result', { transport: 'xhr', status: this.status, route: meta.route }), { once: true });
      return Reflect.apply(originalSend, this, args);
    };
    XMLHttpRequest.prototype.open = wrappedOpen;
    XMLHttpRequest.prototype.send = wrappedSend;
  };
  const scan = () => { if (active) log('ui.snapshot', snapshot()); };
  const start = () => {
    if (active) return;
    active = true;
    log('session.start', { version: VERSION, designMode: !!window.Shopify?.designMode, coverage: 'observable DOM, lifecycle, same-page network metadata, errors' });
    ['click', 'change', 'submit', 'invalid'].forEach(type => attach(document, type, event => log(`interaction.${type}`, { tag: event.target?.localName || '' }), true));
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:select', 'shopify:block:select', 'shopify:block:deselect', 'shopify:section:reorder'].forEach(type => attach(document, type, event => { log('editor.lifecycle', { event: type, section: event.target?.id || '' }); scan(); }, true));
    attach(window, 'error', event => log('script.error', { message: String(event.message || '').slice(0, 240), file: String(event.filename || '').split('/').pop() }, 'error'), true);
    attach(window, 'unhandledrejection', event => log('promise.unhandled', { reason: String(event.reason?.message || event.reason || '').slice(0, 240) }, 'error'), true);
    attach(window, 'resize', () => { clearTimeout(timer); timer = setTimeout(scan, 150); });
    installNetwork();
    observer = new MutationObserver(list => { log('dom.batch', { mutations: list.length }); clearTimeout(timer); timer = setTimeout(scan, 120); });
    observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'disabled', 'aria-expanded', 'data-dawn-base-feature'] });
    scan();
  };
  const stop = () => {
    if (!active) return;
    log('session.stop');
    active = false;
    observer?.disconnect();
    clearTimeout(timer);
    cleanup.splice(0).forEach(fn => safe(fn));
    if (window.fetch === wrappedFetch) window.fetch = originalFetch;
    if (window.XMLHttpRequest) {
      if (XMLHttpRequest.prototype.open === wrappedOpen) XMLHttpRequest.prototype.open = originalOpen;
      if (XMLHttpRequest.prototype.send === wrappedSend) XMLHttpRequest.prototype.send = originalSend;
    }
  };
  const report = () => ({ version: VERSION, active, discarded, records: records.slice(), latest: snapshot(), limitations: ['does not collect request bodies, cookies, tokens or customer values', 'metadata correlation is not proof of business causality', 'no observed log is not a functional pass', 'visual, discount, subscription and order outcomes still require manual verification'] });
  const download = () => { const blob = new Blob([JSON.stringify(report(), null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `dawn-base-debug-${Date.now()}.json`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
  window.DawnBaseDebug = Object.freeze({ version: VERSION, start, stop, scan, report, download, get active() { return active; } });
  if (enabled) start();
})();